/**
 * Activity banner card (MapActivityBanner.vue) — the richest content painter.
 *
 * Two responsibilities, deliberately co-located because they share the same
 * metrics (width / room / responsible info):
 *  - `planBanners`: decide which activities show a banner this frame and measure
 *    each one's width + flip. Called once by the content frame (before labels, so
 *    the label-vs-banner decision is unified). No DOM reflow — measured on canvas.
 *  - `drawBanners`: paint the planned banners (preview square, panel, title +
 *    favourite, responsible + avatar, deadline, state glyph) and record each
 *    card's clickable regions into the hit registry.
 */
import { ItemState } from '@/common';
import {
  effectiveResponsibleUserOf,
  isDeletedUser,
} from '@/utils/helpers/user-helpers';
import { formatDateString } from '@/utils/helpers/various';
import { $t } from '@/utils/i18n/vueI18n';
import { drawIcon, stateIconColor, stateIconName } from '../../draw/icons';
import { cssVar } from '../../draw/palette';
import { roundRect } from '../../draw/shapes';
import { activityGlyphX } from '../../engine/activityGlyphX';
import { withGroupOpacity } from '../../engine/groupOpacity';
import { layoutY } from '../../layout/activityLayout';
import { isSelectedActivity } from './selection';
import type { SceneContext } from '../../engine/SceneContext';
import type { BannerPlan, Rect } from '../../engine/types';
import type { MapActivity } from '@/store/ioStore';

interface ResponsibleInfo {
  func?: string;
  userName?: string;
  userId?: string;
  alert: boolean;
  hasPic: boolean;
  avatarUrl: string;
  initials: string;
}

/**
 * Which activities show a banner this frame, and their measured width + flip.
 * Mirrors MapActivityBanner.showBanner: hovered/selected always; in 'auto' mode
 * also when there is room (measured on-canvas — no DOM reflow); link context is
 * hover-only.
 *
 * The room test is layout-aware: in the flow layout it only counts neighbours
 * that are VERTICALLY near the banner card (|ΔY| < 60 — the card is 60px tall),
 * so a fanned-out cluster can show several full banners side by side where the
 * legacy workstream-order test (any horizontal neighbour blocks) showed none. A
 * second pass then drops an auto banner whose card would still overlap an auto
 * banner to its left (deterministic: the leftmost wins).
 */
export function planBanners(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  visible: MapActivity[],
): BannerPlan[] {
  const { map } = scene;
  const labelMode = map.labelMode;
  const link = map.selectedContext === 'link';
  const flow = scene.layout?.mode === 'flow';
  const collapsed = scene.layout?.collapsedIds;
  const result: BannerPlan[] = [];

  for (const activity of visible) {
    // A collapsed group member shows no banner — its group marker stands in
    // (even when selected: the marker carries the selection ring instead).
    if (collapsed?.has(activity.id)) {
      continue;
    }
    const hovered = map.hoveredActivity === activity.id;
    const hexHovered = map.hexagonHoveredActivity === activity.id;
    const selected = isSelectedActivity(scene, activity);
    // `sticky` drives whether the banner SHOWS (and survives auto-room collision)
    // — banner-card hover keeps it shown and reachable, unchanged. `hexHovered`
    // is held across the hexagon→card dead zone (WI #5570), so including it keeps
    // a flipped card drawn while the cursor crosses to it (its hit region stays
    // alive, no visibility blink). The FLIP side is decided separately below.
    const sticky = hovered || selected || hexHovered;
    const show = link
      ? hovered
      : labelMode === 'auto'
        ? sticky ||
          (flow
            ? hasBannerRoomFlow(ctx, scene, activity)
            : hasBannerRoom(ctx, scene, activity))
        : sticky;
    if (!show) {
      continue;
    }
    const width = bannerWidth(ctx, scene, activity);
    const base = scene.screenX(map.activityPos(activity));
    // WI #5570: the flip side is latched to hexagon-hover OR selection only —
    // never to banner-card hover. Card-hover used to feed `sticky` here, so a
    // card near the right edge oscillated: hovering it flipped it left, which
    // slid the card out from under the cursor → un-hover → un-flip → it jumped
    // back right under the cursor → re-flip … (the reported flicker). The
    // interaction layer holds `hexagonHoveredActivity` across the dead zone
    // between the hexagon and its flipped card, so moving from the hexagon onto
    // the now-left card keeps it flipped (icons stay reachable, no re-flicker).
    const flipDriver = hexHovered || selected;
    const flipped = flipDriver && base + 35 + width > scene.width;
    result.push({ activity, width, flipped });
  }

  if (flow) {
    return dropOverlappingAutoBanners(scene, result);
  }

  return result;
}

/**
 * Flow-layout pass 2: with Y-aware room tests two AUTO banners can still
 * collide card-on-card (each one's test sees the other's hexagon/label, not its
 * 60px-tall card). Walk left→right and drop the right one of any colliding
 * pair; hovered/selected banners always survive.
 */
function dropOverlappingAutoBanners(
  scene: SceneContext,
  banners: BannerPlan[],
): BannerPlan[] {
  const { map } = scene;
  const ordered = banners
    .map((plan) => {
      const x = scene.screenX(map.activityPos(plan.activity));

      return {
        plan,
        x,
        // Card span: preview square + panel; a flipped (sticky, near the right
        // edge) card extends LEFT of the hexagon instead.
        left: plan.flipped ? x - 34 - plan.width : x - 35,
        right: plan.flipped ? x + 35 : x + 35 + plan.width,
        // Target-space Y, like hasBannerRoomFlow — keeps the kept/dropped set
        // stable while a re-fan glide is in flight.
        y:
          scene.layout?.targetY(plan.activity.id) ??
          layoutY(scene, plan.activity.id),
        sticky:
          map.hoveredActivity === plan.activity.id ||
          map.hexagonHoveredActivity === plan.activity.id ||
          isSelectedActivity(scene, plan.activity),
      };
    })
    .sort((a, b) => a.x - b.x);

  const kept: typeof ordered = [];
  for (const entry of ordered) {
    let collides = false;
    if (!entry.sticky) {
      // Card widths vary, so earlier entries' right edges aren't monotone —
      // scan all kept banners (there are at most a handful on screen).
      for (const left of kept) {
        if (left.right >= entry.left && Math.abs(left.y - entry.y) < 60) {
          collides = true;
          break;
        }
      }
    }
    if (!collides) {
      kept.push(entry);
    }
  }

  return kept.map((entry) => entry.plan);
}

/**
 * Flow-layout banner room: the card (60px tall, from 35px right of the hexagon)
 * fits when no OTHER activity sits inside its rectangle's vertical reach —
 * ahead within the card's span (+35 clearance), or behind with an inline label
 * reaching past the card's left edge (−40 clearance). Neighbours fanned to
 * other rows (|ΔY| ≥ 60) don't block, which is the whole point of the layout.
 */
function hasBannerRoomFlow(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  activity: MapActivity,
): boolean {
  const layout = scene.layout;
  if (!layout) {
    return false;
  }
  const { map } = scene;
  const points = layout.points;
  const base = scene.screenX(map.activityPos(activity));
  // Target-space Y on both sides (layout points carry targets too), so the
  // room decision is stable for the whole glide instead of flickering while
  // the candidate's animated Y travels between rows.
  const y = layout.targetY(activity.id);
  const rightEdge = base + 35 + bannerWidth(ctx, scene, activity) + 35;
  // Furthest back a neighbour's drawn inline label can plausibly reach (the
  // SVG banner scan used the same 700px backward window).
  const windowLeft = base - 700;

  // Binary search for the first point inside the scan window (points are
  // sorted by screen x).
  let lo = 0;
  let hi = points.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid].x < windowLeft) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  for (let i = lo; i < points.length; i++) {
    const p = points[i];
    if (p.x > rightEdge) {
      break;
    }
    if (p.id === activity.id || Math.abs(p.y - y) >= 60) {
      continue;
    }
    if (p.x > base) {
      // Ahead: any same-row neighbour inside the card's span blocks.
      return false;
    }
    if (p.labelReach > base - 40) {
      // Behind: its inline label reaches into the card's left clearance.
      return false;
    }
  }

  return true;
}

/** Paint the planned banners and rebuild the hit-region list for this frame. */
export function drawBanners(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  // Banner hit-regions are reset by the renderer at the top of paintContent (so a
  // throw in an earlier painter can't leave stale regions); we only add here.
  const banners = scene.content?.banners ?? [];
  // Record each shown banner's state-glyph content-space centre X so the
  // state-picker popover can anchor the dropdown beside the glyph (WI #5560).
  // The glyph sits at the card's far edge, whose X depends on the painter-
  // measured width and flip (mirrors drawBanner's `left` + glyph offset, in
  // content space), so interaction code can't recompute it. Templates draw no
  // state glyph, so they contribute nothing.
  const glyphX = new Map<string, number>();
  if (!scene.map.isTemplate) {
    for (const b of banners) {
      const contentBase = scene.map.scaleX(scene.map.activityPos(b.activity));
      const left = b.flipped ? contentBase - 34 - b.width : contentBase + 35;
      glyphX.set(b.activity.id, left + b.width - 20);
    }
  }
  activityGlyphX.set(glyphX);
  // Filter-shadowed banners render at 0.2 — as GROUP opacity (SVG
  // `.activity-group.shadowed { opacity: 0.2 }`), so the card's overlapping parts
  // (square, panel, icons, avatar, text) flatten before compositing instead of
  // darkening each other. Draw the dimmed ones first through one batched
  // group-opacity pass, then the un-dimmed (hovered/selected/in-focus) banners on
  // top — which also makes them win hit-testing (registered last → topmost).
  const dimmed = banners.filter((b) =>
    scene.map.isShadowedActivityId(b.activity.id),
  );
  const normal = banners.filter(
    (b) => !scene.map.isShadowedActivityId(b.activity.id),
  );
  if (dimmed.length) {
    withGroupOpacity(ctx, 0.2, (g) => {
      for (const banner of dimmed) {
        drawBanner(g, scene, banner.activity, banner.width, banner.flipped);
      }
    });
  }
  for (const banner of normal) {
    drawBanner(ctx, scene, banner.activity, banner.width, banner.flipped);
  }
}

/** MapActivityBanner room test (forceShowBanner), in content (scaleX) space. */
function hasBannerRoom(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  activity: MapActivity,
): boolean {
  const { map, io } = scene;
  const base = map.scaleX(activity.originalPosition);
  const minNextStartX = base + bannerWidth(ctx, scene, activity) + 35;

  const wsActs = io.activitiesInWorkstream(activity.workstream);
  const idx = wsActs.findIndex((a) => a.id === activity.id);
  const next = idx >= 0 ? wsActs[idx + 1] : undefined;
  const prev = idx >= 0 ? wsActs[idx - 1] : undefined;
  const nextStart = next ? map.scaleX(next.originalPosition) : Infinity;
  const textSize = map.textSize(activity.id) || 100;
  const prevEnd = prev
    ? map.scaleX(prev.originalPosition) + textSize
    : -Infinity;

  return nextStart > minNextStartX && prevEnd < base;
}

/** Approximate the DOM banner's natural width from its three content rows. */
function bannerWidth(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  activity: MapActivity,
): number {
  const isTemplate = scene.map.isTemplate;
  const titleFont = '600 11px Roboto, sans-serif';
  const font = '11px Roboto, sans-serif';

  const title = activity.mirrored
    ? `${activity.title} || ${activity.sourceProjectTitle ?? ''}`
    : activity.title;
  let titleW = scene.text.measure(ctx, title, titleFont);
  if (!isTemplate && !activity.mirrored && activity.isFavorite) {
    titleW += 4 + 14;
  }
  let left = titleW;

  const info = responsibleInfo(scene, activity);
  if (info.func || info.userName) {
    let w = info.func ? scene.text.measure(ctx, info.func, font) : 0;
    if (info.userName) {
      w +=
        2 +
        (info.alert ? 14 + 2 : scene.text.measure(ctx, '-', font) + 4) +
        (info.hasPic ? 16 + 2 : 0) +
        scene.text.measure(ctx, info.userName, font);
    }
    left = Math.max(left, w);
  }

  if (!isTemplate) {
    const deadline = $t('activity.dueDate', {
      date: bannerDeadline(scene, activity),
    });
    left = Math.max(left, scene.text.measure(ctx, deadline, font));
  } else {
    const posLabel = bannerPositionLabel(scene, activity);
    if (posLabel) {
      left = Math.max(left, scene.text.measure(ctx, posLabel, font));
    }
  }

  left = Math.min(left, 600);
  const right = isTemplate ? 0 : 20;

  return 10 + left + 15 + right + 10;
}

function drawBanner(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  activity: MapActivity,
  width: number,
  flipped: boolean,
): void {
  const { map, user } = scene;
  const isTemplate = map.isTemplate;
  const base = scene.screenX(map.activityPos(activity));
  const top = layoutY(scene, activity.id) - 30;
  const selected = isSelectedActivity(scene, activity);
  const colors = bannerColors(scene, activity, selected);
  const left = flipped ? base - 34 - width : base + 35;

  // Dimming for filter-shadowed banners is applied by the caller as group
  // opacity (see drawBanners) so the card's overlapping parts don't darken each
  // other — this function always draws at full opacity.
  ctx.save();

  // Preview square around the hexagon (SVG `.square`, 78×59, state-stroked).
  const squareX = (flipped ? base - 43 : base - 35) + 0.5;
  ctx.lineWidth = 1;
  ctx.strokeStyle = colors.border;
  roundRect(ctx, squareX, top + 0.5, 78, 59, 4);
  ctx.stroke();

  // Panel.
  roundRect(ctx, left, top, width, 60, 4);
  ctx.fillStyle = colors.bg;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = colors.border;
  ctx.stroke();

  const padX = left + 10;
  const row1 = top + 14;
  const row2 = top + 30;
  const row3 = top + 46;
  const leftMax = width - 35 - (isTemplate ? 0 : 20);
  const font = '11px Roboto, sans-serif';

  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';

  // Title (600) + favourite star.
  const fav = !isTemplate && !activity.mirrored && activity.isFavorite;
  ctx.font = '600 11px Roboto, sans-serif';
  ctx.fillStyle = colors.text;
  const title = activity.mirrored
    ? `${activity.title} || ${activity.sourceProjectTitle ?? ''}`
    : activity.title;
  const titled = scene.text.truncate(
    ctx,
    title,
    Math.max(0, leftMax - (fav ? 18 : 0)),
  );
  ctx.fillText(titled.text, padX, row1);
  if (fav) {
    drawIcon(
      ctx,
      'Star',
      padX + titled.width + 4 + 7,
      row1,
      14,
      cssVar('--color-warning-400', '#FDB022'),
      true,
    );
  }

  // Responsible: function name, separator/alert, avatar, user name.
  const info = responsibleInfo(scene, activity);
  ctx.font = font;
  ctx.fillStyle = colors.text;
  let cx = padX;
  if (info.func) {
    ctx.fillText(info.func, cx, row2);
    cx += scene.text.measure(ctx, info.func, font);
  }
  if (info.userName) {
    cx += 2;
    if (info.alert) {
      drawIcon(ctx, 'AlertCircle', cx + 7, row2, 14, colors.text);
      cx += 14 + 2;
    } else {
      ctx.fillStyle = colors.text;
      ctx.fillText('-', cx, row2);
      cx += scene.text.measure(ctx, '-', font) + 4;
    }
    if (info.hasPic) {
      // Fetch on demand for shown banners only; the avatarUrls watcher repaints.
      if (info.userId) {
        void user.loadUserAvatar(info.userId, true);
      }
      drawAvatar(ctx, scene, cx + 8, row2, info.avatarUrl, info.initials);
      cx += 16 + 2;
    }
    ctx.fillStyle = colors.text;
    ctx.textAlign = 'left';
    ctx.fillText(info.userName, cx, row2);
  }

  // Deadline (projects only). A blue glow on hover signals it's clickable
  // (MapActivityBanner `.deadline:hover` drop-shadow + cursor:pointer).
  let deadlineRect: Rect | undefined;
  if (!isTemplate) {
    ctx.font = font;
    ctx.fillStyle = colors.text;
    const deadlineText = $t('activity.dueDate', {
      date: bannerDeadline(scene, activity),
    });
    ctx.save();
    applyHoverGlow(ctx, scene, activity.id, 'deadline');
    ctx.fillText(deadlineText, padX, row3);
    ctx.restore();
    deadlineRect = {
      x: padX,
      y: row3 - 9,
      w: scene.text.measure(ctx, deadlineText, font),
      h: 18,
    };
    // Spinner beside the deadline while it saves (MapActivityBanner `UiSpinner`).
    if (scene.state.deadlineLoadingActivityId === activity.id) {
      drawDeadlineSpinner(ctx, padX + deadlineRect.w + 9, row3, colors.text);
    }
  } else {
    // Templates: the position line (positions mode only). Plain text, not
    // clickable (MapActivityBanner renders it as a `.responsible` span).
    const posLabel = bannerPositionLabel(scene, activity);
    if (posLabel) {
      ctx.font = font;
      ctx.fillStyle = colors.text;
      ctx.fillText(posLabel, padX, row3);
    }
  }

  // Right-side state glyph (projects only), with the same clickable hover glow.
  let stateRect: Rect | undefined;
  if (!isTemplate) {
    ctx.save();
    applyHoverGlow(ctx, scene, activity.id, 'state');
    drawIcon(
      ctx,
      stateIconName(activity.state, activity.status),
      left + width - 20,
      top + 31,
      20,
      stateIconColor(activity.state, activity.status),
    );
    ctx.restore();
    stateRect = { x: left + width - 30, y: top + 21, w: 20, h: 20 };
  }

  // Record clickable regions (screen space). The body is the text card panel
  // ONLY — NOT the preview square around the hexagon — so a click on the card
  // can't deselect, while a click on the hexagon still selects the activity.
  scene.hits.addBanner({
    activityId: activity.id,
    panel: { x: left, y: top, w: width, h: 60 },
    state: stateRect,
    deadline: deadlineRect,
  });

  ctx.restore();
}

/**
 * Apply the SVG "clickable" hover affordance (a blue drop-shadow glow) to the
 * banner region currently under the cursor. Mirrors `.deadline:hover` /
 * `.state-icon-wrapper:hover { filter: drop-shadow(0 0 4px #007bffcc) }`.
 */
function applyHoverGlow(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  activityId: string,
  region: 'state' | 'deadline',
): void {
  if (
    scene.state.hoverBannerActivityId === activityId &&
    scene.state.hoverBannerRegion === region
  ) {
    ctx.shadowColor = 'rgba(0, 123, 255, 0.8)';
    ctx.shadowBlur = 4;
  }
}

/**
 * Small rotating arc beside the deadline while it saves (MapActivityBanner's
 * `UiSpinner`). The renderer keeps the content layer repainting while
 * `deadlineLoadingActivityId` is set, so reading `performance.now()` here spins it.
 */
function drawDeadlineSpinner(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
): void {
  const angle = (performance.now() / 1000) * Math.PI * 2;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(cx, cy, 5, angle, angle + Math.PI * 1.5);
  ctx.stroke();
  ctx.restore();
}

/** 16px responsible avatar: clipped image when loaded, else an initials disc. */
function drawAvatar(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
  cx: number,
  cy: number,
  url: string,
  initials: string,
): void {
  const radius = 8;
  const img = url ? scene.images.get(url) : null;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.closePath();
  if (img) {
    ctx.clip();
    ctx.drawImage(img, cx - radius, cy - radius, 16, 16);
  } else {
    ctx.fillStyle = cssVar('--color-bluegray-200', '#C8CCE5');
    ctx.fill();
    ctx.fillStyle = '#363f72';
    ctx.font = '8px Roboto, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, cx, cy + 0.5);
  }
  ctx.restore();
}

/**
 * Read-only banner responsible info. Pure on purpose: it runs for every
 * auto-mode candidate during the room-measure pass, so it must NOT kick off
 * avatar fetches (drawBanner triggers those only for banners that actually show).
 */
function responsibleInfo(
  scene: SceneContext,
  activity: MapActivity,
): ResponsibleInfo {
  const { io, user } = scene;
  let func: string | undefined;
  try {
    func = activity.mirrored
      ? activity.responsible
      : activity.responsible
        ? io.function(activity.responsible)?.name
        : undefined;
  } catch {
    func = undefined;
  }

  // Only the person swaps on a done activity; the function name above is
  // deliberately left as-is.
  const ru = effectiveResponsibleUserOf(activity, activity.responsibleUser);
  const out: ResponsibleInfo = {
    func,
    userName: undefined,
    userId: undefined,
    alert: !!activity.isSpecialAssignment,
    hasPic: false,
    avatarUrl: '',
    initials: '',
  };
  if (!ru) {
    return out;
  }
  if (typeof ru === 'string') {
    out.userName = ru;

    return out;
  }
  out.userId = ru.id;
  if (isDeletedUser(ru)) {
    // A deleted account keeps its place on completed work, but with a neutral
    // caption and no avatar fetch. `hasPic` stays false, so the banner renders
    // the caption alone — exactly as it already does for any user without a
    // profile picture; the initials are only read if a disc is ever drawn.
    out.userName = $t('users.deletedUser');
    out.initials = '?';

    return out;
  }
  out.userName =
    ru.first_name && ru.last_name
      ? `${ru.first_name} ${ru.last_name}`
      : (ru.username ?? ru.email ?? undefined);
  out.hasPic = !!ru.has_profile_picture;
  out.initials =
    ((ru.first_name?.[0] ?? '') + (ru.last_name?.[0] ?? '')).toUpperCase() ||
    '?';
  if (out.hasPic) {
    out.avatarUrl = user.avatarUrls[ru.id] ?? '';
  }

  return out;
}

/**
 * Banner deadline text (MapActivityBanner `realtimeDeadline`). While a gesture
 * displaces the activity horizontally — move tool / long-press drag (single or
 * multi-selection), space and compress tools — the date previews the deadline
 * the activity WOULD get if dropped at its drawn position: positions are
 * day-indexed, so the stored deadline shifts by the drawn-vs-rest delta of
 * `activityPos`. The SVG banner only previews the move tool; the delta form
 * covers every displacing gesture with the projection as the single source.
 */
function bannerDeadline(scene: SceneContext, activity: MapActivity): string {
  const { map } = scene;
  if (map.isTemplate) {
    return '';
  }
  const delta = Math.round(
    map.activityPos(activity) - activity.originalPosition,
  );
  if (delta === 0) {
    return formatDateString(activity.deadline);
  }
  const date = new Date(activity.deadline);
  date.setUTCDate(date.getUTCDate() + delta);

  return formatDateString(date.toISOString());
}

/**
 * Template position label for the banner's third row (MapActivityBanner
 * `realtimePosition`): shown only for templates in 'positions' timeline mode.
 * While the activity is being moved it reflects the live (offset-adjusted)
 * position. Returns null when no position line should be drawn.
 */
function bannerPositionLabel(
  scene: SceneContext,
  activity: MapActivity,
): string | null {
  const { map } = scene;
  if (!map.isTemplate || map.timeLineTemplateMode !== 'positions') {
    return null;
  }

  // `activityPos` IS the live position (rest = originalPosition, displaced by
  // move/space/compress gestures) — same single-source rule as bannerDeadline.
  return `${$t('activity.position')}: ${Math.round(map.activityPos(activity))}`;
}

function bannerColors(
  scene: SceneContext,
  activity: MapActivity,
  selected: boolean,
): { bg: string; text: string; border: string } {
  if (selected) {
    const blue = cssVar('--color-bluelight-500', '#0BA5EC');

    return { bg: blue, text: '#FFFFFF', border: blue };
  }
  switch (activity.state as ItemState) {
    case 'unplanned':
      return {
        bg: '#FFFFFF',
        text: cssVar('--color-primary-900', '#15314D'),
        border: cssVar('--color-gray-200', '#E4E7EC'),
      };
    case 'planned':
      return {
        bg: cssVar('--color-bluegray-100', '#D5D9EB'),
        text: cssVar('--color-bluegray-600', '#3E4784'),
        border: cssVar('--color-bluegray-200', '#C8CCE5'),
      };
    case 'underway':
      if (activity.status === 'green') {
        return {
          bg: cssVar('--color-success-50', '#ECFDF3'),
          text: cssVar('--color-success-900', '#054F04'),
          border: cssVar('--color-success-500', '#12B76A'),
        };
      }
      if (activity.status === 'yellow') {
        return {
          bg: cssVar('--color-warning-50', '#FFF7E0'),
          text: cssVar('--color-warning-900', '#7A2E0E'),
          border: cssVar('--color-warning-300', '#FEC84B'),
        };
      }
      if (activity.status === 'red') {
        return {
          bg: cssVar('--color-error-50', '#FEF3F2'),
          text: cssVar('--color-error-900', '#7A271A'),
          border: cssVar('--color-error-300', '#FDA29B'),
        };
      }

      return {
        bg: cssVar('--color-bluegray-50', '#F8F9FC'),
        text: '#000000',
        border: cssVar('--color-bluegray-400', '#717BBC'),
      };
    default:
      // 'done' and any fallback use the base banner styling.
      return {
        bg: '#FFFFFF',
        text: cssVar('--color-gray-800', '#1D2939'),
        border: '#cccccc',
      };
  }
}
